import React, { useRef } from "react";

/**
 * A two-or-three option segmented control.
 *
 * Deliberately not a nested MUI Base <Tabs>. The bottom nav needs hand-written
 * `aria-selected:` Tailwind literals precisely because MUI Base owns the
 * selected state and only exposes it through the DOM attribute. Here selection
 * lives in React state, so the selected styling is an ordinary conditional over
 * two complete literal strings — strictly less Tailwind risk, and no
 * <TabsList>/<TabPanel> wrappers forced onto the page body.
 *
 * These are toggle buttons (`aria-pressed`), not tabs. A real tab pattern would
 * need `role="tablist"`, `aria-controls` and matching `role="tabpanel"` ids
 * threaded through every caller; a half-implemented one is worse than an honest
 * button group.
 *
 * @param {Object} props
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {Array<{ value: string, label: string }>} props.options
 * @param {string} props.ariaLabel
 * @param {string} [props.selectedClassName] Complete Tailwind literal. Never
 *   interpolate a variant prefix into this.
 * @param {string} [props.className] Extra classes for the container.
 */
const SegmentedControl = ({
  value,
  onChange,
  options,
  ariaLabel,
  selectedClassName = "bg-white text-gray-900 shadow-sm",
  className = "",
}) => {
  const containerRef = useRef(null);

  // Arrow keys move between options with wraparound, matching what people
  // expect from a segmented control even though these are toggle buttons.
  const handleKeyDown = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    const currentIndex = options.findIndex((option) => option.value === value);
    if (currentIndex === -1) return;

    event.preventDefault();
    const offset = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (currentIndex + offset + options.length) % options.length;

    onChange(options[nextIndex].value);
    containerRef.current?.children[nextIndex]?.focus();
  };

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={`flex gap-1 p-1 bg-gray-100 rounded-xl ${className}`}
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-gray-400 ${
              isSelected
                ? selectedClassName
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
